import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, ArrowRight, Star } from "lucide-react";
import { api } from "@/lib/api";

export default function Team() {
  const [team, setTeam] = React.useState([]);
  React.useEffect(() => { api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);

  const founder = team.find((m) => m.is_founder);
  const others = team.filter((m) => !m.is_founder);

  return (
    <div>
      <section className="hero-mesh grain">
        <div className="container-wide py-20 md:py-28">
          <div className="overline">Our people</div>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">The team behind Skyverra Consulting.</h1>
          <p className="mt-6 max-w-2xl text-[color:var(--sky-muted)] text-lg leading-relaxed">
            Senior engineers, product minds, and consultants who quietly ship enterprise-grade
            software for our clients. Get to know the people you&apos;ll be working with.
          </p>
        </div>
      </section>

      {/* Founder spotlight */}
      {founder && (
        <section className="section">
          <div className="container-wide grid gap-10 lg:grid-cols-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-[color:var(--sky-cyan)]/20 blur-3xl rounded-full" />
                <div className="relative aspect-[4/5] rounded-md overflow-hidden border border-[color:var(--sky-border)] max-w-md bg-white">
                  {founder.photo_url ? (
                    <img src={founder.photo_url} alt={founder.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[color:var(--sky-navy)]/10" />
                  )}
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 badge-cyan"><Star size={12} className="fill-current" /> Founder</div>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl tracking-tight">{founder.name}</h2>
              <p className="mt-2 text-[color:var(--sky-navy)] font-semibold text-lg">{founder.role}</p>
              <p className="mt-6 text-[color:var(--sky-muted)] leading-relaxed max-w-2xl text-base md:text-lg">{founder.bio}</p>
              <div className="mt-8 flex items-center gap-4">
                {founder.linkedin && <a href={founder.linkedin} target="_blank" rel="noreferrer" className="btn-outline text-sm"><Linkedin size={14} /> LinkedIn</a>}
                {founder.email && <a href={`mailto:${founder.email}`} className="btn-outline text-sm"><Mail size={14} /> Email</a>}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Rest of the team */}
      <section className="section bg-white border-y border-[color:var(--sky-border)]">
        <div className="container-wide">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="overline">Team</div>
              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl tracking-tight">
                {others.length > 0 ? `${others.length} more people driving Skyverra forward.` : "Growing our team."}
              </h2>
            </div>
            <Link to="/careers" className="btn-outline text-sm">See open roles <ArrowRight size={14} /></Link>
          </div>

          {others.length === 0 && (
            <div className="card-flat p-10 text-center text-[color:var(--sky-muted)]">
              We&apos;re a lean, senior team focused on delivery. Employees will be introduced here as we grow — check back soon or head to <Link to="/careers" className="text-[color:var(--sky-navy)] font-semibold">Careers</Link> to join us.
            </div>
          )}

          {others.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {others.map((m) => (
                <div key={m.id} className="card-flat overflow-hidden">
                  <div className="aspect-square overflow-hidden bg-[color:var(--sky-navy)]/10">
                    {m.photo_url && <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-lg">{m.name}</h3>
                    <p className="text-sm text-[color:var(--sky-navy)] font-semibold">{m.role}</p>
                    <p className="text-sm text-[color:var(--sky-muted)] mt-2 leading-relaxed">{m.bio}</p>
                    <div className="mt-4 flex items-center gap-3">
                      {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="text-[color:var(--sky-navy)] hover:text-[color:var(--sky-cyan)]"><Linkedin size={16} /></a>}
                      {m.email && <a href={`mailto:${m.email}`} className="text-[color:var(--sky-navy)] hover:text-[color:var(--sky-cyan)]"><Mail size={16} /></a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-wide">
          <div className="relative rounded-md overflow-hidden bg-[color:var(--sky-navy)] text-white p-10 md:p-14">
            <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-[color:var(--sky-cyan)]/25 blur-3xl" />
            <div className="relative grid gap-8 lg:grid-cols-2 items-center">
              <div>
                <div className="overline text-white/70">Join us</div>
                <h2 className="mt-3 font-display font-black text-3xl md:text-4xl leading-tight tracking-tight">Want to build with senior engineers on real enterprise software?</h2>
              </div>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                <Link to="/careers" className="btn-primary bg-[color:var(--sky-cyan)] text-[#062133] hover:bg-white">See open roles <ArrowRight size={16} /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
